import React, { useMemo } from 'react';
import { isActivityItem } from '../activityTypes.js';
import { ITEM_TYPES } from '../config.js';
import { getItemSlotStyle } from '../systems/itemPositionStyle.js';

const PARTICLE_COUNT = 14;

const TONE_CLASS = Object.freeze({
    good: 'lp-game__pop-effect--good',
    hot: 'lp-game__pop-effect--hot',
    bonus: 'lp-game__pop-effect--bonus',
    activity: 'lp-game__pop-effect--activity',
    bad: 'lp-game__pop-effect--bad',
});

/**
 * @param {object} item
 */
export function resolveBurstTone(item) {
    if (item.type === ITEM_TYPES.BAD_ITEM) return 'bad';
    if (item.type === ITEM_TYPES.HOT_LEAD) return 'hot';
    if (item.type === ITEM_TYPES.BONUS) return 'bonus';
    if (isActivityItem(item)) return 'activity';
    return 'good';
}

function buildParticles(burstId, isActivity) {
    const baseDistance = isActivity ? 52 : 64;
    const sizeBoost = isActivity ? 0 : 1;

    return Array.from({ length: PARTICLE_COUNT }, (_, index) => ({
        id: `${burstId}-${index}`,
        angle: (360 / PARTICLE_COUNT) * index + 8,
        distance: baseDistance + (index % 4) * 12,
        size: 5 + (index % 3) + sizeBoost,
        delay: index * 12,
    }));
}

/**
 * Particle burst at the live card position (card itself pops in place via FloatingItem).
 * @param {{ id: string, item: object, tone: keyof typeof TONE_CLASS }} burst
 * @param {{ width: number, height: number }} boardSize
 */
export function PopBurst({ burst, boardSize = { width: 0, height: 0 } }) {
    const isActivity = isActivityItem(burst.item);
    const particles = useMemo(() => buildParticles(burst.id, isActivity), [burst.id, isActivity]);
    const toneClass = TONE_CLASS[burst.tone] ?? TONE_CLASS.good;

    return (
        <div
            className={`lp-game__pop-effect ${toneClass}${isActivity ? ' lp-game__pop-effect--activity-size' : ''}`}
            style={{
                ...getItemSlotStyle(burst.item, boardSize),
                zIndex: (burst.item.zIndex ?? 4) + 3,
            }}
            aria-hidden
        >
            <div className="lp-game__pop-effect-fx">
                <span className="lp-game__burst-ring" />
                <span className="lp-game__burst-flash" />
                {particles.map((particle) => (
                    <span
                        key={particle.id}
                        className="lp-game__burst-particle"
                        style={{
                            '--lp-burst-angle': `${particle.angle}deg`,
                            '--lp-burst-distance': `${particle.distance}px`,
                            '--lp-burst-size': `${particle.size}px`,
                            '--lp-burst-delay': `${particle.delay}ms`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
